import React, { useMemo } from 'react';
import { Star } from 'lucide-react';
import { AgencyRatingStars } from '../Components/AgencyRatingStars';

type ChipComponent = React.ComponentType<{ readonly text: string }>;

interface LPTestimonialsProps {
  readonly isMobile: boolean;
  readonly Chip: ChipComponent;
}

interface TestimonialItem {
  readonly text: string;
  readonly name: string;
  readonly role: string;
  readonly img?: string;
}

export const LPTestimonials = ({ isMobile, Chip }: LPTestimonialsProps): React.JSX.Element => {
  const TESTIMONIALS_DATA: readonly TestimonialItem[] = [
    {
      text: "Worky gave our agency one place to manage care shifts, approvals, time tracking and invoicing. We no longer have to jump between spreadsheets and separate tools.",
      name: "Bilal Ahmed",
      role: "Healthcare Staffing Operations Manager",
    },
    {
      text: "The Digital TimeClock changed how we track care shifts. We can verify attendance in real time instead of guessing whether a shift was covered, with a clear record to refer back to.",
      name: "Aliza Khan",
      role: "Healthcare Agency Recruiter",
    },
    {
      text: "Once a care shift is submitted in Worky, pay and billing follow automatically. Having that connected workflow removed hours of admin from our week.",
      name: "Farhan Siddiqui",
      role: "Talent Acquisition Director, Healthcare Agency",
    },
    {
      text: "Coordinating care shifts is smoother with Worky. When schedules change at the last minute, we can adjust quickly and keep our agency’s operations on track.",
      name: "Saman Malik",
      role: "Nurse Staffing Coordinator, Healthcare Agency",
    },
    {
      text: "We used to piece together separate scheduling, payroll and invoicing tools. Worky brought those tasks into one platform that fits the way our home-care agency operates.",
      name: "Omar Raza",
      role: "Managing Director, Home-Care Staffing Agency",
    },
    {
      text: "Different client rules made payroll preparation a manual task. Worky applies breaks, premiums and overtime automatically based on the client, so we no longer have to edit them before payroll runs.",
      name: "Sana Sheikh",
      role: "Healthcare Recruitment Team Lead",
    },
    // Added a few more to ensure columns are full for the demo
    {
      text: "Billing for care shifts is finally consistent. Our invoice PDFs follow each client contract’s rules, without days spent reconciling individual line items.",
      name: "David Chen",
      role: "Healthcare Staffing Agency Owner",
    },
    {
      text: "Our care workforce includes employees and contractors with different rates. Worky Pay handles that complexity and has made payroll more predictable.",
      name: "Sarah Jenkins",
      role: "Director of Nursing Recruitment, Healthcare Agency",
    },
    {
      text: "Worky gives us a clear view of payroll and billing for care shifts. With verified time and automated rules behind the numbers, it’s easier to protect our margins.",
      name: "Michael Ross",
      role: "Healthcare Staffing Finance Manager",
    },
    {
      text: "Before a review, we no longer have to chase down timesheets. Worky keeps approvals and verified time tied to each care shift, giving us a clear audit trail.",
      name: "Priya Nair",
      role: "Clinical Compliance Manager, Healthcare Agency",
    },
    {
      text: "One admin can now manage more care staff because our operations follow a consistent process. Worky helps our agency grow without having to grow the admin team at the same pace.",
      name: "James Carter",
      role: "Healthcare Staffing Business Development Lead",
    },
    {
      text: "We used to move between QuickBooks, payroll tools and scheduling software. Bringing our care operations into one system reduced errors and the constant rework.",
      name: "Ayesha Patel",
      role: "Healthcare Agency Partnerships Manager",
    },
    {
      text: "Tracking care-shift hours, preparing payroll and creating invoices used to involve a lot of manual admin. Worky has reduced that work, and our team spends less time on data entry.",
      name: "Daniel Brooks",
      role: "Healthcare Staffing Payroll Lead",
    },
    {
      text: "Worky keeps each care shift moving through the same steps: submission, approval and verified time. That consistency helps us keep operations on track as the workload grows.",
      name: "Hina Mahmood",
      role: "Senior Healthcare Recruitment Consultant",
    },
    {
      text: "Our managers can review care shifts quickly, and the approved information flows into pay and client billing. We no longer have extra steps between approval and processing.",
      name: "Laura Bennett",
      role: "Home-Care Agency Operations Lead",
    },
    {
      text: "Connecting care-shift tracking, verified time, payroll and invoicing gave our leadership a clearer view of the agency. We can finally see the full operational picture in one place.",
      name: "Marcus Hill",
      role: "Managing Partner, Healthcare Recruitment Agency",
    },
    {
      text: "With a more connected workflow for care shifts, our team has fewer exceptions to sort out. Clients receive clearer bills, and we have fewer billing disputes to manage.",
      name: "Emily Foster",
      role: "Nurse Placement Manager, Healthcare Agency",
    },
    {
      text: "What stood out was connecting each care shift to time tracking, pay and invoicing. That end-to-end automation is exactly what an agency needs as it grows.",
      name: "Noah Williams",
      role: "Healthcare Staffing Director",
    },
  ];

  const TestimonialCard = ({ item }: { readonly item: TestimonialItem }): React.JSX.Element => (
    <div className="lp-testimonials-card">
      <div className="lp-testimonials-card-rating" role="img" aria-label="5 out of 5 stars">
        {Array.from({ length: 5 }, (_, index) => (
          <Star key={index} size={16} fill="currentColor" aria-hidden="true" />
        ))}
      </div>
      <div className="lp-testimonials-card-text">{item.text}</div>
      <div className="lp-testimonials-card-footer">
        {item.img ? (
          <img src={item.img} alt={item.name} className="lp-testimonials-card-avatar" />
        ) : (
          <span className="lp-testimonials-card-avatar lp-testimonials-card-initials" aria-hidden="true">
            {item.name.trim().split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()}
          </span>
        )}
        <div className="lp-testimonials-card-meta">
          <span className="lp-testimonials-card-name">{item.name}</span>
          <span className="lp-testimonials-card-role">{item.role}</span>
        </div>
      </div>
    </div>
  );

  // Distribute data into 3 columns
  const columns = useMemo((): TestimonialItem[][] => {
    const col1: TestimonialItem[] = [];
    const col2: TestimonialItem[] = [];
    const col3: TestimonialItem[] = [];

    TESTIMONIALS_DATA.forEach((item: TestimonialItem, i: number) => {
      if (i % 3 === 0) col1.push(item);
      else if (i % 3 === 1) col2.push(item);
      else col3.push(item);
    });

    return [col1, col2, col3];
  }, []);

  return (
    <section className="lp-testimonials-wrapper">
      {/* Header */}
      <div className="lp-testimonials-header lp-testimonials-header-agency">
        <Chip text="testimonials" />
        <h2 className="lph-carousel-heading lp-testimonials-trust-title">
          <span>Five-Star Software.</span>
          <span>Trusted by 1,500+ Agencies.</span>
        </h2>
        <div className="lp-product-container-inner-text lp-testimonials-trust-description" style={{ textAlign: 'center' }}>
          From home-care providers to healthcare staffing teams, more than 1,500 agencies use Worky to run their day-to-day operations. Bring scheduling, time tracking, payroll and invoicing together with software rated five stars.
        </div>
        <div className="lp-testimonials-rating"><AgencyRatingStars /></div>
      </div>

      {/* Grid */}
      <div className={isMobile ? 'lp-testimonials-grid-mobile' : 'lp-testimonials-grid'}>
        {columns.map((colItems: TestimonialItem[], colIndex: number) => (
          <div key={colIndex} className="lp-testimonials-col-container">
            <div className="lp-testimonials-scroll-track">
              {colItems.map((item: TestimonialItem, i: number) => (
                <TestimonialCard key={`orig-${colIndex}-${i}`} item={item} />
              ))}
              {colItems.map((item: TestimonialItem, i: number) => (
                <TestimonialCard key={`dupe-${colIndex}-${i}`} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
